import { Flex, Spinner, VStack, useToast } from "@chakra-ui/react";
import { useMemo, useEffect } from "react";
import { SlideInHeader } from "../components/SlideInHeader";
import { BundleDetailsBody } from "./BundleDetailsBody";
import { SLIDE_IN_MENUS } from "../../../screens/root/constants";
import { getBundleDetail, useSlideInMenu } from "../hooks/useSlideInMenu";
import { useQuery } from "@tanstack/react-query";
import { useEntityCache } from "../../../hooks/useEntityCache";
import { FunnelConfigTypes, FunnelContextArea } from "../../funnel/types";
import { useFunnel } from "../../funnel/hooks/useFunnel";
import { AnimatePresence, motion } from "framer-motion";
import {
  useSlideInFunnel,
  funnelAnimationVariants,
  menuAnimationVariants,
  transitionConfig,
} from "../hooks/useSlideInFunnel";

const MotionFlex = motion(Flex);

interface BundleDetailsWrapperProps {
  onCloseMenu?: () => void;
  onFunnelChange?: (funnelConfig: FunnelConfigTypes | undefined) => void;
}

export function BundleDetailsWrapper({
  onCloseMenu,
  onFunnelChange,
}: BundleDetailsWrapperProps) {
  const { bundleUuid, goBack } = useSlideInMenu();
  const { saveEntityToCache } = useEntityCache();
  const toast = useToast();

  const { funnelConfig, setFunnelConfig, scrollContainerRef, handleBack } =
    useSlideInFunnel({ onFunnelChange, goBack });

  const { data: fetchedBundleData, error } = useQuery({
    enabled: !!bundleUuid,
    queryKey: ["bundleDetail", bundleUuid],
    queryFn: async () => {
      const response = await getBundleDetail(bundleUuid ?? "");
      const bundle = response?.data.payload.results;

      if (bundle) {
        saveEntityToCache(bundle);
      }
      return response;
    },
    networkMode: "offlineFirst",
  });

  useEffect(() => {
    if (error) {
      console.error("Failed to load bundle details:", error);
      toast({
        title: "Error loading bundle",
        status: "error",
        duration: 5000,
      });
      onCloseMenu?.();
    }
  }, [error, toast, onCloseMenu]);

  const bundleFunnelConfig = useMemo(() => {
    return {
      ...funnelConfig,
      context: {
        ...funnelConfig?.context,
        modalContext: FunnelContextArea.default,
      },
    };
  }, [funnelConfig]);

  const { funnelBody, funnelFooter } = useFunnel({
    type: bundleFunnelConfig?.type,
    context: bundleFunnelConfig?.context,
    options: bundleFunnelConfig?.options,
    onClose: () => setFunnelConfig(undefined),
    setFunnelConfig: setFunnelConfig,
  });

  if (!fetchedBundleData) {
    return (
      <Flex justify="center" align="center" height="100%">
        <Spinner color="blaze.blaze" size="lg" />
      </Flex>
    );
  }

  const bundle = fetchedBundleData?.data.payload.results;

  return (
    <VStack
      height="100%"
      spacing={0}
      align="stretch"
      id="gcSlideInMenuContentContainer"
    >
      <SlideInHeader
        handleBack={handleBack}
        openMenu={SLIDE_IN_MENUS.bundleDetails}
        onCloseMenu={onCloseMenu}
      />
      <AnimatePresence initial={false} mode="wait">
        {funnelBody ? (
          <MotionFlex
            key="funnel"
            ref={scrollContainerRef}
            {...funnelAnimationVariants}
            transition={transitionConfig}
            className="styled-scrollbar"
            p="24px"
            gap="24px"
            flexGrow={1}
            overflowY="auto"
            maxWidth="100%"
            flexDirection="column"
            mb={funnelConfig?.options?.isModal ? "96px" : "24px"}
          >
            {funnelBody}
            {funnelFooter}
          </MotionFlex>
        ) : (
          <MotionFlex
            key="menu"
            {...menuAnimationVariants}
            transition={transitionConfig}
            className="styled-scrollbar"
            flexGrow={1}
            overflowY="auto"
            maxWidth="100%"
            flexDirection="column"
          >
            <BundleDetailsBody
              bundle={bundle}
              setFunnelConfig={setFunnelConfig}
            />
          </MotionFlex>
        )}
      </AnimatePresence>
    </VStack>
  );
}
